// ABOUTME: The plate shown when the server names a screen the client does not have: a quick-refresh message with Reload and a way out.
// ABOUTME: Past the built frontier, in any build, it reads "not built yet" instead and Back works.

import { useEffect } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  CROSSING_PLATE_BG, CROSSING_PLATE_BORDER, CROSSING_PLATE_SHADOW, CROSSING_HEADLINE_INK, CROSSING_SUB_INK,
} from '../../../theme/crossingTokens';
import { visuallyHidden } from './visuallyHidden';

export const FALLBACK_HEADLINE = 'This part needs a quick refresh.';
const RELOAD_LABEL = 'Reload';
const DASHBOARD_LABEL = 'Back to dashboard';
const BACK_LABEL = 'Back';

interface FallbackPlateProps {
  screenId: string;
  // True when the screen exists in the definition but is not built on the runner yet.
  notBuiltYet: boolean;
  canGoBack: boolean;
  headlineId: string;
  // The same one-line notice and live-region text the plate carries, so a stale move onto an unbuilt screen is still announced.
  notice?: string | null;
  announcement?: string | null;
  onReload: () => void;
  onBack: () => void;
  onDashboard: () => void;
}

export default function FallbackPlate({ screenId, notBuiltYet, canGoBack, headlineId, notice, announcement, onReload, onBack, onDashboard }: FallbackPlateProps) {
  useEffect(() => {
    if (!notBuiltYet) console.warn('Crossing screen is not in the runner registry', { screenId });
  }, [screenId, notBuiltYet]);

  return (
    <Box sx={{ position: 'fixed', left: 0, right: 0, bottom: 16, zIndex: 10, display: 'flex', justifyContent: 'center', px: 2, pointerEvents: 'none' }}>
      <Box
        data-testid="runner-fallback-plate"
        sx={{
          pointerEvents: 'auto', width: '100%', maxWidth: 900, boxSizing: 'border-box', backgroundColor: CROSSING_PLATE_BG,
          border: `1px solid ${CROSSING_PLATE_BORDER}`, borderRadius: '12px', boxShadow: CROSSING_PLATE_SHADOW, padding: '14px 18px',
          display: 'flex', flexDirection: 'column', gap: '10px',
        }}
      >
        <Typography id={headlineId} component="h1" sx={{ fontFamily: 'Newsreader, serif', fontSize: '21px', fontWeight: 600, color: CROSSING_HEADLINE_INK }}>
          {FALLBACK_HEADLINE}
        </Typography>
        {notBuiltYet && (
          <Typography sx={{ fontSize: '13px', color: CROSSING_SUB_INK }}>{"This part isn't ready yet. Go Back, or head to your dashboard."}</Typography>
        )}
        <Box role="alert" data-testid="runner-alert" />
        {notice && <Typography data-testid="runner-notice" sx={{ fontSize: '13px', color: CROSSING_SUB_INK }}>{notice}</Typography>}
        <Box role="status" aria-live="polite" aria-atomic="true" data-testid="runner-live-region" sx={visuallyHidden}>{announcement}</Box>
        <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {notBuiltYet ? (
            <Button variant="contained" aria-disabled={!canGoBack || undefined} onClick={() => { if (canGoBack) onBack(); }}>{BACK_LABEL}</Button>
          ) : (
            <Button variant="contained" onClick={onReload}>{RELOAD_LABEL}</Button>
          )}
          <Button variant="outlined" onClick={onDashboard}>{DASHBOARD_LABEL}</Button>
        </Box>
      </Box>
    </Box>
  );
}
